/**
 * useFxRates — fetch display-currency FX rates with defensive fallbacks.
 *
 * Replaces the prior `useFxRates` import from `useGroupReports`. The
 * previous version could return `undefined` on error paths, which React
 * Query forbids and logs the warning:
 *
 *   "Query data cannot be undefined. Affected query key: [\"fx-rates\"]"
 *
 * This version guarantees the queryFn returns a typed object on every
 * code path — even on network failure or backend 404.
 *
 * Endpoint: `GET /fx/rates?base=GHS` (Phase 2 backend). If the FX
 * backend isn't installed yet, this hook gracefully returns
 * `{rates: null, cachedAt: null, isStale: true}` and the app continues
 * to work in GHS-only mode (DisplayCurrencyContext falls back).
 */
import { useQuery } from "@tanstack/react-query";
import api from "@/services/api";

export interface FxRatesResponse {
  base: string;
  rates: Record<string, number> | null;
  cachedAt: string | null;
  source?: string;
  isStale?: boolean;
}

const fallback = (base: string): FxRatesResponse => ({
  base,
  rates: null,
  cachedAt: null,
  isStale: true,
});

export function useFxRates(base = "GHS", enabled = true) {
  return useQuery<FxRatesResponse>({
    queryKey: ["fx-rates", base],
    enabled,
    queryFn: async () => {
      try {
        const res = await api.get<FxRatesResponse>("/fx/rates", {
          params: { base },
        });
        const body = res.data;
        if (!body || typeof body !== "object") return fallback(base);
        return {
          base: body.base || base,
          rates: body.rates ?? null,
          cachedAt: body.cachedAt ?? null,
          source: body.source,
          isStale: body.isStale ?? !body.rates,
        };
      } catch {
        // Never throw out of the queryFn. Always return a structured
        // value so React Query doesn't log the undefined warning.
        return fallback(base);
      }
    },
    staleTime: 5 * 60 * 1000, // 5 min
    gcTime: 30 * 60 * 1000, // 30 min
    retry: 1,
    refetchOnWindowFocus: false,
  });
}

export default useFxRates;
